class ResponseError extends Error {
  constructor(message, response) {
    super(message);
    this.name = 'ResponseError';
    this.response = response;
  }
}

export default class FetchClient {
  constructor({ maxRetryTime = 10000, debug = false } = {}) {
    this.maxRetryTime = maxRetryTime;
    this.debug = debug;
  }

  async request(url, options = {}) {
    const startTime = Date.now();
    let retryCount = 0;

    while (Date.now() - startTime < this.maxRetryTime) {
      try {
        const response = await fetch(url, options);

        if (!response.ok) {
          throw new ResponseError('Fetch did not work properly', response);
        }

        return await this.parseResponse(response);
      } catch (error) {
        if (this.debug) {
          console.warn('Debug mode: bypassing error handling.');
          return 'SUCCESS';
        }

        this.handleErrors(error?.response);

        const delay = Math.pow(2, retryCount) * 100;
        if (Date.now() - startTime + delay >= this.maxRetryTime) break;

        await new Promise((resolve) => setTimeout(resolve, delay));
        retryCount++;
      }
    }

    return 'FAILED';
  }

  async get(url) {
    return this.request(url, { method: 'GET' });
  }

  async post(url, params) {
    return this.request(url, {
      method: 'POST',
      body: params,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'Accept': '*/*',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
  }

  async parseResponse(response) {
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return 'SUCCESS';
    }
  }

  handleErrors(response) {
    if (!response) {
      console.error('NETWORK_ERROR or NO_RESPONSE');
      return;
    }

    if (response.ok) return;

    const errorMessages = {
      400: 'INVALID_TOKEN',
      401: 'UNAUTHORIZED',
      404: 'NOT_FOUND',
      500: 'INTERNAL_SERVER_ERROR',
      502: 'BAD_GATEWAY',
    };

    console.error(errorMessages[response.status] || 'UNHANDLED_ERROR');
  }
}

// const api = new FetchClient({ maxRetryTime: 10000, debug: false });

// GET
// const data = await api.get('/api/data');

// POST
// const result = await api.post('/api/submit', 'key=value');